import {Avatar, Input, Radio, Spin} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {lookup_lookupInfoByValue_Api} from '../../apis'
import useAsyncAction from '../../modules/asyncCache/useAsyncAction'
import DrawDialog from '../drawer/dialog/DrawDialog'
import {IdentifierTypes} from '../../views/AppLocals/appFieldTypes'
import getAvatar from '../../helpers/getAvatar'
import {BsPersonFill} from 'react-icons/bs'
import getTitle from '../../helpers/getTitle'
import getAliasName from '../../helpers/getAliasName'
import {Link} from 'react-router-dom'
import getLinkToDetail from '../../helpers/getLinkToDetail'
import {HiCheckCircle, HiXCircle} from 'react-icons/hi'
import {MdOutlineManageSearch} from 'react-icons/md'

const LOOKUP_DEBOUNCE = 700

const SEARCH_MODES = {
    email: 'email',
    identifier: 'identifier-type',
}

const ResultCard = memo(({userInfo, isLoading, searchMode, onChoose, t}) => {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-6">
                <Spin size="default"/>
            </div>
        )
    }
    if (userInfo === null) {
        return (
            <div className="flex flex-col items-center gap-2 py-6 text-color-400">
                <MdOutlineManageSearch size={36} className="opacity-40"/>
                <span className="text-sm italic">{t('enter value to search')}</span>
            </div>
        )
    }
    if (!userInfo?.name) {
        return (
            <div className="flex items-center gap-2 py-4 px-2 text-orange-500">
                <HiXCircle size={16} className="flex-shrink-0"/>
                <span className="text-sm italic">
                    {searchMode === SEARCH_MODES.email
                        ? t('not found email description')
                        : t('not found id code description')}
                </span>
            </div>
        )
    }
    const {owner, email} = userInfo
    const patronSaint = getAliasName(owner, 'patron_saint')
    return (
        <div className="flex flex-col gap-2">
            <span className="flex items-center gap-1 text-xs text-color-400 italic">
                <HiCheckCircle size={13} className="text-green-500 flex-shrink-0"/>
                {searchMode === SEARCH_MODES.email
                    ? t('this email address is associated with the account')
                    : t('this identifier is linked to the account')}
            </span>
            <div className="p-3 flex items-center gap-3 border border-green-400 rounded-xl">
                <Avatar
                    size={40}
                    src={getAvatar(owner)}
                    icon={<BsPersonFill size={18} className="text-color-500"/>}
                    className="flex-shrink-0 flex flex-center background-200"
                />
                <div className="flex flex-col flex-1 min-w-0">
                    <Link
                        to={getLinkToDetail(owner)}
                        target="_blank"
                        className="font-bold text-color-000 no-underline truncate">
                        {!!patronSaint && (
                            <span className="mr-1 font-medium text-xs text-color-400 italic capitalize">
                                {patronSaint}
                            </span>
                        )}
                        {getTitle(owner)}
                    </Link>
                    <span className="text-xs text-color-500 italic leading-tight truncate">{email}</span>
                </div>
                <button
                    type="button"
                    onClick={onChoose}
                    className="flex-shrink-0 px-3 py-1 rounded-md text-xs font-semibold bg-primary text-white hover:opacity-85 cursor-pointer border-0">
                    {t('choose')}
                </button>
            </div>
        </div>
    )
})

// Tách nội dung form ra component riêng + memo
// → Khi Toggle/drawer state thay đổi, chỉ DrawDialog re-render, không kéo theo UserLookupContent
const UserLookupContent = memo(({handleApply, toggle}) => {
    const t = useTranslate()

    const [searchMode, setSearchMode] = useState(SEARCH_MODES.email)
    const [identifierType, setIdentifierType] = useState(
        () => Object.values(IdentifierTypes)?.[0]?.value ?? 'identifier-type'
    )
    const [inputValue, setInputValue] = useState('')
    const [userInfo, setUserInfo] = useState(null)
    const [isSearching, setIsSearching] = useState(false)

    const debounceRef = useRef(null)
    const searchModeRef = useRef(searchMode)
    const identifierTypeRef = useRef(identifierType)
    const inputValueRef = useRef(inputValue)

    const {response, success, handleAsyncAction: lookupInfo} = useAsyncAction({
        apiInfo: lookup_lookupInfoByValue_Api,
    })

    // Dùng ref thay vì sync qua useEffect — tránh thêm render cycle
    const lookupRef = useRef(lookupInfo)
    lookupRef.current = lookupInfo  // luôn fresh, không cần useEffect
    searchModeRef.current = searchMode
    identifierTypeRef.current = identifierType
    inputValueRef.current = inputValue

    // actionIdRef: track action hiện tại để tránh xử lý response của action cũ
    // (khi unmount/remount, Redux cache vẫn giữ success=true của lần search trước)
    const actionIdRef = useRef(null)
    const pendingActionIdRef = useRef(null)

    useEffect(() => {
        // Guard: chỉ xử lý response của action đang chờ, bỏ qua cache cũ
        if (pendingActionIdRef.current === null) return
        setIsSearching(false)
        if (success) {
            setUserInfo(_.get(response, 'data', {}))
        } else {
            setUserInfo({})
        }
    }, [success, response])

    const triggerLookup = useCallback((value, mode, idType) => {
        // Cancel debounce cũ nếu còn
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        if (!value || !value.trim()) {
            setUserInfo(null)
            setIsSearching(false)
            return
        }
        // Set searching trước debounce — UX phản hồi ngay, không chờ 700ms
        setIsSearching(true)
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            const action = lookupRef.current({}, {
                search_type: 'user',
                input_type: mode === SEARCH_MODES.email ? 'email' : 'identifier_code',
                input_value: value.trim(),
                ...(mode !== SEARCH_MODES.email ? {identifier_type: idType} : {}),
            })
            // Track action id để effect biết đây là response mới
            pendingActionIdRef.current = action?.asyncId ?? Date.now()
        }, LOOKUP_DEBOUNCE)
    }, [])

    const handleInputChange = useCallback((e) => {
        const value = e?.target?.value ?? ''
        setInputValue(value)
        setUserInfo(null)
        triggerLookup(value, searchModeRef.current, identifierTypeRef.current)
    }, [triggerLookup])

    const handleModeChange = useCallback((mode) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setSearchMode(mode)
        searchModeRef.current = mode
        setInputValue('')
        setUserInfo(null)
        setIsSearching(false)
    }, [])

    const handleIdentifierTypeChange = useCallback((idType) => {
        setIdentifierType(idType)
        identifierTypeRef.current = idType
        setUserInfo(null)
        const current = inputValueRef.current
        if (current) {
            triggerLookup(current, searchModeRef.current, idType)
        }
    }, [triggerLookup])

    const suffix = useMemo(() => {
        if (isSearching) return <Spin size="small"/>
        if (!inputValue) return null
        if (userInfo?.name) return <HiCheckCircle size={16} className="text-green-500"/>
        if (userInfo !== null) return <HiXCircle size={16} className="text-orange-400"/>
        return null
    }, [isSearching, inputValue, userInfo])

    const handleChoose = useCallback(() => {
        handleApply({...userInfo})
        if (_.isFunction(toggle)) toggle(false)
    }, [handleApply, userInfo, toggle])

    return (
        <div className="flex flex-col gap-5">
            {/* Tab mode */}
            <div className="flex justify-center">
                <Radio.Group
                    value={searchMode}
                    optionType="button"
                    buttonStyle="solid"
                    className="custom-radio-button"
                    onChange={(e) => handleModeChange(e.target.value)}>
                    <Radio.Button value={SEARCH_MODES.email}>{t('email')}</Radio.Button>
                    <Radio.Button value={SEARCH_MODES.identifier}>{t('identifier code')}</Radio.Button>
                </Radio.Group>
            </div>

            {/* Input */}
            <div className="flex flex-col gap-2">
                {searchMode === SEARCH_MODES.identifier && (
                    <Radio.Group
                        value={identifierType}
                        onChange={(e) => handleIdentifierTypeChange(e.target.value)}
                        className="custom-radio-button-sm"
                        optionType="button"
                        buttonStyle="solid">
                        {Object.values(IdentifierTypes).map((e, i) => (
                            <Radio.Button
                                key={i}
                                value={e.value}
                                style={{padding: '6px 8px', height: 32}}>
                                {t(e.label)}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                )}
                <Input
                    value={inputValue}
                    suffix={suffix}
                    onChange={handleInputChange}
                    placeholder={t(
                        searchMode === SEARCH_MODES.email
                            ? 'email placeholder'
                            : 'ex: id code'
                    )}
                />
            </div>

            {/* Kết quả */}
            <ResultCard
                t={t}
                userInfo={userInfo}
                isLoading={isSearching}
                searchMode={searchMode}
                onChoose={handleChoose}
            />
        </div>
    )
})

const UserLookupComponent = ({handleApply, children}) => {
    const t = useTranslate()

    return (
        <DrawDialog
            width={600}
            component={children}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('account lookup')}
                </div>
            }>
            {(isToggle, toggle) => {
                // Guard: không render content khi drawer đóng
                if (!isToggle) return null

                return (
                    <UserLookupContent
                        handleApply={handleApply}
                        toggle={toggle}
                    />
                )
            }}
        </DrawDialog>
    )
}

export default UserLookupComponent